export const TIERS = [
  { name: 'Baseline', min: 0, max: 19, desc: 'Basic category participation with limited reporting, education access, and low-touch support.', color: '#999', cls: 't1' },
  { name: 'Developing', min: 20, max: 39, desc: 'Emerging category opportunity with moderate support, reporting participation, and limited activation eligibility.', color: '#60a5fa', cls: 't2' },
  { name: 'Growth', min: 40, max: 59, desc: 'Developing category value with recurring education, seasonal activations, supplier coordination, and structured reporting.', color: '#34d399', cls: 't3' },
  { name: 'Signature', min: 60, max: 79, desc: 'Strong category opportunity with strategic planning, recurring activations, enhanced reporting, and higher supplier-facing value.', color: '#fb923c', cls: 't4' },
  { name: 'Flagship', min: 80, max: 100, desc: 'Highest-touch category support with leadership advisory, complex activations, enhanced analytics, supplier business reviews, and category innovation support.', color: '#C9A668', cls: 't5' },
];

export const CATEGORIES = [
  { id: 'spirits', name: 'Liquor', icon: '🥃', field: 'spiritsSales' },
  { id: 'beer', name: 'Beer', icon: '🍺', field: 'beerSales' },
  { id: 'wine', name: 'Wine', icon: '🍷', field: 'wineSales' },
  { id: 'na', name: 'Non-Alcoholic', icon: '🥤', field: 'naSales' },
];

export const VENUE_TYPES = ['Restaurant', 'Bar', 'Hotel', 'Resort', 'Nightlife', 'Private Club', 'Casino', 'Other'];
export const CUISINES = ['American', 'Italian', 'French', 'Mexican / Latin', 'Asian / Japanese', 'Steakhouse', 'Seafood', 'Mediterranean', 'Southern', 'Fusion', 'Indian', 'Middle Eastern', 'Farm-to-Table', 'Other'];

const TOURISM_HIGH = ['miami', 'las vegas', 'vegas', 'new york', 'nyc', 'manhattan', 'los angeles', 'la', 'san francisco', 'sf', 'honolulu', 'hawaii', 'maui', 'orlando', 'key west', 'nashville', 'new orleans', 'nola', 'scottsdale', 'aspen', 'vail', 'napa', 'charleston', 'savannah', 'san diego', 'austin', 'denver', 'chicago', 'boston', 'washington dc', 'dc', 'south beach', 'wynwood', 'brickell', 'waikiki', 'beverly hills', 'west hollywood', 'santa monica', 'malibu', 'palm beach', 'hamptons'];
const TOURISM_MED = ['philadelphia', 'philly', 'atlanta', 'dallas', 'houston', 'seattle', 'portland', 'minneapolis', 'phoenix', 'tampa', 'fort lauderdale', 'pittsburgh', 'charlotte', 'raleigh', 'indianapolis', 'columbus', 'salt lake city', 'baltimore', 'san antonio', 'sacramento', 'memphis', 'milwaukee', 'louisville', 'atlantic city', 'park city', 'sedona', 'hilton head', 'amelia island'];
const SEASONAL_HIGH = ['hamptons', 'cape cod', 'nantucket', 'jersey shore', 'outer banks', 'hilton head', 'key west', 'aspen', 'vail', 'telluride', 'park city', 'mammoth', 'tahoe', 'palm beach', 'scottsdale', 'myrtle beach', 'ocean city', 'rehoboth', 'montauk', 'bar harbor', 'newport', 'mackinac', 'door county'];
const SEASONAL_MED = ['miami', 'south beach', 'orlando', 'nashville', 'new orleans', 'charleston', 'savannah', 'san diego', 'austin', 'denver', 'boston', 'chicago', 'dc', 'phoenix', 'tampa', 'fort lauderdale'];

function detect(market, high, med) {
  const m = (market || '').toLowerCase();
  if (high.some(t => m.includes(t))) return 'High';
  if (med.some(t => m.includes(t))) return 'Medium';
  return 'Low';
}

export function detectTourism(market) { return detect(market, TOURISM_HIGH, TOURISM_MED); }
export function detectSeasonality(market) { return detect(market, SEASONAL_HIGH, SEASONAL_MED); }

const lmh = v => ({ Low: 20, Medium: 55, High: 90 }[v] || 20);

export function revScore(sales, catId) {
  if (catId === 'na') { if (sales >= 500000) return 100; if (sales >= 250000) return 85; if (sales >= 100000) return 70; if (sales >= 50000) return 55; if (sales >= 25000) return 40; if (sales >= 10000) return 25; return 10; }
  if (catId === 'beer') { if (sales >= 2000000) return 100; if (sales >= 1000000) return 85; if (sales >= 500000) return 70; if (sales >= 250000) return 55; if (sales >= 100000) return 40; if (sales >= 50000) return 25; return 10; }
  if (sales >= 5000000) return 100; if (sales >= 2500000) return 85; if (sales >= 1000000) return 70; if (sales >= 500000) return 55; if (sales >= 250000) return 40; if (sales >= 100000) return 25; return 10;
}

export function ratioScore(r) {
  if (r >= 0.5) return 100; if (r >= 0.35) return 80; if (r >= 0.2) return 60; if (r >= 0.1) return 40; if (r >= 0.05) return 25; return 10;
}

export function calcActivation(unit) {
  const ts = { High: 90, Medium: 55, Low: 20 }[detectTourism(unit.market)] || 20;
  const ss = { High: 75, Medium: 50, Low: 25 }[detectSeasonality(unit.market)] || 25;
  const tb = { Hotel: 15, Resort: 20, Nightlife: 15, Casino: 15, 'Private Club': 10 }[unit.type] || 0;
  const ob = unit.outlets >= 3 ? 15 : unit.outlets >= 2 ? 8 : 0;
  const sb = (+unit.avgAlcSpend >= 50) ? 20 : (+unit.avgAlcSpend >= 25) ? 10 : 0;
  const cb = (+unit.coversPerWeek >= 2000) ? 20 : (+unit.coversPerWeek >= 1000) ? 12 : (+unit.coversPerWeek >= 500) ? 6 : 0;
  return Math.min(100, Math.round(
    lmh(unit.activation) * 0.14 + lmh(unit.social) * 0.11 + lmh(unit.luxury) * 0.11 + lmh(unit.bevComplexity) * 0.10 +
    lmh(unit.events) * 0.09 + lmh(unit.suppliers) * 0.08 + lmh(unit.fbLeadership) * 0.06 + lmh(unit.training) * 0.04 + lmh(unit.reporting) * 0.04 +
    ts * 0.07 + ss * 0.04 + tb * 0.04 + ob * 0.03 + sb * 0.03 + cb * 0.02
  ));
}

export function getTier(score) {
  for (let i = TIERS.length - 1; i >= 0; i--) {
    if (score >= TIERS[i].min) return { ...TIERS[i], num: i + 1 };
  }
  return { ...TIERS[0], num: 1 };
}

const REV_FLOORS = { spirits: 2500000, wine: 2500000, beer: 1000000, na: 500000 };

export function scoreUnit(unit) {
  const total = +unit.totalSales || 0;
  if (!total) return null;
  const act = calcActivation(unit);
  return CATEGORIES.map(cat => {
    const sales = +(unit[cat.field] || 0);
    const ratio = total > 0 ? sales / total : 0;
    const rs = revScore(sales, cat.id);
    const rts = ratioScore(ratio);
    let final = Math.round(rs * 0.45 + rts * 0.20 + act * 0.35);
    if (sales >= REV_FLOORS[cat.id]) final = Math.max(final, 60);
    return { ...cat, sales, ratio, revScore: rs, ratScore: rts, actScore: act, finalScore: Math.min(100, final), tier: getTier(Math.min(100, final)) };
  });
}

export function newUnit(name) {
  return {
    name: name || 'Unit 1', market: '', address: '', type: 'Restaurant', cuisine: 'American', outlets: 1, pos: 'No',
    totalSales: '', spiritsSales: '', wineSales: '', beerSales: '', naSales: '',
    avgGuestSpend: '', avgAlcSpend: '', coversPerWeek: '',
    luxury: 'Medium', activation: 'Medium', reporting: 'Medium', suppliers: 'Medium',
    training: 'Medium', events: 'Medium', social: 'Medium', fbLeadership: 'Medium', bevComplexity: 'Medium',
    results: null,
  };
}

export const PRESETS = [
  {
    id: 'casual_dining', label: 'Casual Dining Restaurant', type: 'Restaurant', cuisine: 'American', outlets: 1,
    totalSales: 850000, spiritsSales: 250000, wineSales: 200000, beerSales: 300000, naSales: 100000,
    avgGuestSpend: 45, avgAlcSpend: 18, coversPerWeek: 1200,
    luxury: 'Low', activation: 'Medium', reporting: 'Low', suppliers: 'Low',
    training: 'Medium', events: 'Low', social: 'Medium', fbLeadership: 'Low', bevComplexity: 'Low',
  },
  {
    id: 'fine_dining', label: 'Fine Dining Restaurant', type: 'Restaurant', cuisine: 'French', outlets: 1,
    totalSales: 2800000, spiritsSales: 600000, wineSales: 1600000, beerSales: 150000, naSales: 450000,
    avgGuestSpend: 185, avgAlcSpend: 75, coversPerWeek: 600,
    luxury: 'High', activation: 'High', reporting: 'Medium', suppliers: 'Medium',
    training: 'Medium', events: 'High', social: 'High', fbLeadership: 'Medium', bevComplexity: 'High',
  },
  {
    id: 'steakhouse', label: 'Steakhouse', type: 'Restaurant', cuisine: 'Steakhouse', outlets: 1,
    totalSales: 3500000, spiritsSales: 900000, wineSales: 2000000, beerSales: 350000, naSales: 250000,
    avgGuestSpend: 150, avgAlcSpend: 65, coversPerWeek: 900,
    luxury: 'High', activation: 'High', reporting: 'Medium', suppliers: 'High',
    training: 'Medium', events: 'High', social: 'Medium', fbLeadership: 'Medium', bevComplexity: 'High',
  },
  {
    id: 'cocktail_bar', label: 'Cocktail Bar / Lounge', type: 'Bar', cuisine: 'American', outlets: 1,
    totalSales: 1200000, spiritsSales: 850000, wineSales: 150000, beerSales: 150000, naSales: 50000,
    avgGuestSpend: 55, avgAlcSpend: 42, coversPerWeek: 800,
    luxury: 'Medium', activation: 'High', reporting: 'Low', suppliers: 'Medium',
    training: 'High', events: 'Medium', social: 'High', fbLeadership: 'Low', bevComplexity: 'High',
  },
  {
    id: 'sports_bar', label: 'Sports Bar', type: 'Bar', cuisine: 'American', outlets: 1,
    totalSales: 900000, spiritsSales: 250000, wineSales: 50000, beerSales: 500000, naSales: 100000,
    avgGuestSpend: 35, avgAlcSpend: 20, coversPerWeek: 1500,
    luxury: 'Low', activation: 'Medium', reporting: 'Low', suppliers: 'Low',
    training: 'Low', events: 'Medium', social: 'Medium', fbLeadership: 'Low', bevComplexity: 'Low',
  },
  {
    id: 'hotel_fb', label: 'Hotel F&B', type: 'Hotel', cuisine: 'American', outlets: 3,
    totalSales: 4500000, spiritsSales: 1200000, wineSales: 1800000, beerSales: 800000, naSales: 700000,
    avgGuestSpend: 95, avgAlcSpend: 40, coversPerWeek: 2000,
    luxury: 'High', activation: 'High', reporting: 'High', suppliers: 'High',
    training: 'High', events: 'High', social: 'Medium', fbLeadership: 'High', bevComplexity: 'High',
  },
  {
    id: 'resort', label: 'Resort F&B', type: 'Resort', cuisine: 'American', outlets: 5,
    totalSales: 8000000, spiritsSales: 2500000, wineSales: 2500000, beerSales: 1500000, naSales: 1500000,
    avgGuestSpend: 120, avgAlcSpend: 50, coversPerWeek: 3000,
    luxury: 'High', activation: 'High', reporting: 'High', suppliers: 'High',
    training: 'High', events: 'High', social: 'High', fbLeadership: 'High', bevComplexity: 'High',
  },
  {
    id: 'nightclub', label: 'Nightclub', type: 'Nightlife', cuisine: 'Other', outlets: 2,
    totalSales: 3000000, spiritsSales: 2200000, wineSales: 200000, beerSales: 400000, naSales: 200000,
    avgGuestSpend: 75, avgAlcSpend: 60, coversPerWeek: 2500,
    luxury: 'High', activation: 'High', reporting: 'Low', suppliers: 'Medium',
    training: 'Medium', events: 'High', social: 'High', fbLeadership: 'Low', bevComplexity: 'Medium',
  },
  {
    id: 'event_space', label: 'Event Space / Private Dining', type: 'Restaurant', cuisine: 'American', outlets: 1,
    totalSales: 1500000, spiritsSales: 400000, wineSales: 700000, beerSales: 200000, naSales: 200000,
    avgGuestSpend: 110, avgAlcSpend: 45, coversPerWeek: 500,
    luxury: 'Medium', activation: 'Medium', reporting: 'Medium', suppliers: 'Medium',
    training: 'Medium', events: 'High', social: 'Medium', fbLeadership: 'Medium', bevComplexity: 'Medium',
  },
  {
    id: 'brewery', label: 'Brewery / Taproom', type: 'Bar', cuisine: 'American', outlets: 1,
    totalSales: 600000, spiritsSales: 50000, wineSales: 30000, beerSales: 450000, naSales: 70000,
    avgGuestSpend: 30, avgAlcSpend: 22, coversPerWeek: 1000,
    luxury: 'Low', activation: 'Medium', reporting: 'Low', suppliers: 'Low',
    training: 'Low', events: 'Medium', social: 'Medium', fbLeadership: 'Low', bevComplexity: 'Low',
  },
  {
    id: 'wine_bar', label: 'Wine Bar', type: 'Bar', cuisine: 'Mediterranean', outlets: 1,
    totalSales: 800000, spiritsSales: 100000, wineSales: 550000, beerSales: 80000, naSales: 70000,
    avgGuestSpend: 60, avgAlcSpend: 45, coversPerWeek: 600,
    luxury: 'Medium', activation: 'Medium', reporting: 'Low', suppliers: 'Medium',
    training: 'Medium', events: 'Medium', social: 'Medium', fbLeadership: 'Low', bevComplexity: 'High',
  },
  {
    id: 'casino', label: 'Casino F&B', type: 'Casino', cuisine: 'American', outlets: 4,
    totalSales: 6000000, spiritsSales: 2000000, wineSales: 1500000, beerSales: 1500000, naSales: 1000000,
    avgGuestSpend: 65, avgAlcSpend: 35, coversPerWeek: 5000,
    luxury: 'Medium', activation: 'High', reporting: 'High', suppliers: 'High',
    training: 'High', events: 'High', social: 'Medium', fbLeadership: 'High', bevComplexity: 'Medium',
  },
  {
    id: 'private_club', label: 'Private Club', type: 'Private Club', cuisine: 'American', outlets: 2,
    totalSales: 1800000, spiritsSales: 500000, wineSales: 800000, beerSales: 300000, naSales: 200000,
    avgGuestSpend: 90, avgAlcSpend: 40, coversPerWeek: 800,
    luxury: 'High', activation: 'Low', reporting: 'Medium', suppliers: 'Medium',
    training: 'Medium', events: 'Medium', social: 'Low', fbLeadership: 'Medium', bevComplexity: 'Medium',
  },
];

export function genNotes(r, unit) {
  const n = [];
  const tourism = detectTourism(unit.market);
  const season = detectSeasonality(unit.market);
  if (r.sales < 50000) n.push(`Minimal ${r.name.toLowerCase()} revenue — baseline participation.`);
  else if (r.sales >= REV_FLOORS[r.id]) n.push(`Strong ${r.name.toLowerCase()} revenue drives significant supplier-facing value.`);
  else if (r.sales >= 250000) n.push(`Healthy ${r.name.toLowerCase()} volume creates activation opportunity.`);
  if (r.ratio > 0.5) n.push('Dominant category share — signature vertical.');
  if (tourism === 'High') n.push('High-tourism market increases brand exposure.');
  if (season === 'High') n.push('Significant seasonal variability — adaptive planning required.');
  if (+unit.avgAlcSpend >= 50) n.push('Premium alcohol spend signals premiumization opportunity.');
  if (+unit.coversPerWeek >= 1500) n.push('High cover volume amplifies supplier program reach.');
  if (r.actScore >= 70) n.push('Strong activation value enhances supplier engagement.');
  else if (r.actScore < 30) n.push('Limited activation potential — focus on foundations.');
  return n.join(' ') || 'Standard category positioning.';
}
